import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  Combobox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogDescription,
  Toggle,
  Alert,
} from '@eye-able/ui';
import { languages, translationVersion } from '@/i18n.js';
import LoadingSpinner from '@components/LoadingSpinner.jsx';
import type { I18nPostBody, I18nTranslatePostBody } from '@eye-able/types';
import { useTranslationActions } from '@/i18n/hooks/useTranslationActions';
import { useReleaseActions } from '@/i18n/hooks/useReleaseActions';
import { useDomainActions } from '@/i18n/hooks/useDomainActions';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { useAtom } from 'jotai';
import { userDataAtom } from '@/global-store';
import { LanguageRow } from '@/i18n/dialog/LanguageRow';
import {
  type ComboBoxOption,
  useComboboxOptions,
} from '@/i18n/hooks/useComboboxOptions';
import { useKeyStore } from '@/i18n/hooks/useKeyStore';
import { PluralizationOverview } from '@/i18n/dialog/PluralizationOverview';

type Locale = string;

export function InlineTranslationDialog({
  translationKey: key,
  namespace,
  onClose,
}: {
  translationKey?: string;
  namespace?: string;
  onClose: () => void;
}) {
  const [engine, setEngine] = useState<string>();
  const [translations, setTranslations] = useState<Record<Locale, string>>({});
  const [translationsOriginal, setTranslationsOriginal] = useState<
    Record<Locale, string>
  >({});
  const [selectedOption, setSelectedOption] = useState<
    ComboBoxOption | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);

  const [user] = useAtom(userDataAtom);
  const { saveAction, translateAction } = useTranslationActions();
  const { lockStatusAction } = useReleaseActions();
  const { domainAction } = useDomainActions();
  const { i18n } = useInlineTranslation();
  const { createUniqueOption, createUniqueValue, extractNamespaceKey } =
    useComboboxOptions();
  const { getKeyStore, addKey, getFromKeyStore } = useKeyStore();

  const pluralKeysPerLanguageRef = useRef<Record<Locale, string[]>>({});
  const pluralKeysAllRef = useRef<string[]>([]);
  const baseKeyRef = useRef('');

  const hasLanguagePluralKey = useCallback(
    (key: string, language: string) => {
      return i18n.services.pluralResolver
        .getSuffixes(language)
        .some((suffix: string) => key.endsWith(suffix)) as boolean;
    },
    [i18n.services.pluralResolver],
  );

  const availableLanguages = useMemo(() => {
    if (!selectedOption) return [];
    baseKeyRef.current = '';

    const languageKeys = Object.keys(languages);
    const index = languageKeys.findIndex(
      (language) => language === i18n.resolvedLanguage,
    );

    // show current selected language first for editing
    const sortedLanguages = [...languageKeys.splice(index, 1), ...languageKeys];

    // filter out languages that are not having the plural key for the selected key
    if (
      pluralKeysAllRef.current.some((key) => selectedOption.key.endsWith(key))
    ) {
      // key is a plural key (has one of the plural suffixes)

      // calculate the base key
      const keyParts = selectedOption.key.split('_');
      keyParts.pop();
      baseKeyRef.current = keyParts.join('_');

      return sortedLanguages.filter((language) => {
        return (
          hasLanguagePluralKey(selectedOption.key, language) &&
          pluralKeysPerLanguageRef.current[language]?.includes(
            selectedOption.key,
          )
        );
      });
    }

    return sortedLanguages;
  }, [hasLanguagePluralKey, i18n.resolvedLanguage, selectedOption]);

  useEffect(() => {
    if (domainAction.data?.translatorEngines.length === 0) return;
    setEngine(domainAction.data?.translatorEngines[0]);
    if (!key) return;
    const ns = namespace || domainAction.data?.namespaces[0];
    if (!ns) return;

    setSelectedOption(getFromKeyStore(key, ns));
  }, [
    domainAction.data?.namespaces,
    domainAction.data?.translatorEngines,
    getFromKeyStore,
    key,
    namespace,
  ]);

  useEffect(() => {
    pluralKeysAllRef.current = [];
    pluralKeysPerLanguageRef.current = {};

    if (!selectedOption) return;

    setIsLoading(true);
    void i18n
      .loadLanguages(availableLanguages)
      .then(() => {
        const trans: Record<Locale, string> = {};
        availableLanguages.forEach((language) => {
          trans[language] =
            (i18n.getResource(
              language,
              selectedOption.namespace,
              selectedOption.key,
            ) as string) ?? selectedOption.key;

          /*
            3 possible key cases:
            -
            1. key which uses pluralization by passing the count to the translation function
            2. key which does not use pluralization
            3. key which has a plural key name with i18n suffix (_one, _other, _few, _many)
           */

          // don't create a plural key for a plural key
          if (selectedOption.hasPluralization) {
            const pluralForms = i18n.services.pluralResolver
              .getPluralFormsOfKey(language, selectedOption.key)
              // treat _other as basekey ==> fallback
              .filter((key: string) => !key.endsWith('_other')) as string[];

            pluralKeysAllRef.current = [
              ...new Set([...pluralKeysAllRef.current, ...pluralForms]),
            ];
            pluralKeysPerLanguageRef.current[language] = pluralForms;
          }

          if (
            hasLanguagePluralKey(selectedOption.key, language) ||
            selectedOption.hasPluralization
          ) {
            if (!trans[language].includes('{{count}}')) {
              trans[language] = '{{count}} ' + trans[language];
            }
          }
        });

        setTranslations(trans);
        setTranslationsOriginal(trans);
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false));
  }, [availableLanguages, i18n, hasLanguagePluralKey, selectedOption]);
  const allowedLanguages =
    user?.permissions?.inlineTranslations?.allowedLanguages;
  const hasRestrictions = allowedLanguages && Array.isArray(allowedLanguages);

  const canEditLanguage = useCallback(
    (language: string) => {
      return !!(
        allowedLanguages === 'all' || allowedLanguages?.includes(language)
      );
    },
    [allowedLanguages],
  );

  useEffect(() => {
    setTargetLanguages(
      availableLanguages.filter((language) => {
        // puts only languages to the request that are allowed to be edited
        return canEditLanguage(language);
      }),
    );
  }, [availableLanguages, canEditLanguage]);

  const translationKeys = useMemo(() => {
    const newKeys: ComboBoxOption[] = [];
    const keyStore = Array.from(getKeyStore());

    for (let i = keyStore.length - 1; i >= 0; i--) {
      const item = keyStore[i];

      if (!i18n.exists(item.key, { ns: item.namespace })) {
        newKeys.push(item);
        keyStore.splice(i, 1);
      }
    }

    // sort key - place new keys always at the top
    const oldItems = keyStore
      .sort((a, b) => a.key.toLowerCase().localeCompare(b.key.toLowerCase()))
      .map((item) => ({
        label: `${item.key} (${item.namespace})`,
        value: createUniqueValue(item),
      }));
    const newItems = newKeys
      .sort((a, b) => a.key.toLowerCase().localeCompare(b.key.toLowerCase()))
      .map((item) => ({
        label: `[NEW] ${item.key} (${item.namespace})`,
        value: createUniqueValue(item),
      }));

    return [...newItems, ...oldItems];
  }, [createUniqueValue, getKeyStore, i18n]);

  const changes = useMemo(
    () =>
      targetLanguages.filter(
        (language) =>
          translations?.[language] !== translationsOriginal?.[language],
      ),
    [targetLanguages, translations, translationsOriginal],
  );

  const translatorEngines = () =>
    domainAction.data?.translatorEngines.map((e) => ({ label: e, value: e }));

  const onTextChange = (language: string, value: string) => {
    setTranslations((prev) => ({
      ...prev,
      [language]: value,
    }));
  };

  const onRevertClick = (language: string) => {
    setTranslations((prev) => ({
      ...prev,
      [language]: translationsOriginal[language],
    }));
  };

  if (domainAction.isLoading) {
    return <LoadingSpinner />;
  }

  const translate = async (input: string, sourceLanguage: string) => {
    try {
      setIsLoading(true);
      const result = await translateAction.mutateAsync({
        input,
        sourceLanguage,
        targetLanguage: targetLanguages,
        engine,
      } as I18nTranslatePostBody);
      setTranslations((prev) => ({ ...prev, ...result }));
    } finally {
      setIsLoading(false);
    }
  };

  const saveTranslations = async () => {
    try {
      if (selectedOption?.key && translations) {
        setIsLoading(true);
        await saveAction.mutateAsync({
          namespace: selectedOption.namespace,
          key: selectedOption.key,
          version: translationVersion,
          translations: targetLanguages.map((language) => ({
            locale: language,
            translation: translations[language],
          })),
        } as I18nPostBody);
        targetLanguages.forEach((language) => {
          // updates the frontend values
          i18n.addResource(
            language,
            selectedOption.namespace,
            selectedOption.key,
            translations[language],
          );
          setTranslationsOriginal(translations);
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onLanguageToggle = (checked: boolean, language: string) => {
    if (checked) {
      setTargetLanguages((prev) => [...new Set([...prev, language])]);
    } else {
      setTargetLanguages((prev) => prev.filter((lang) => lang !== language));
    }
  };

  const onSelect = (uniqueValue: string) => {
    // todo: change interface for onSelect event of combobox to get the full selected
    // object instead just the value because the value can be the same
    // for multiple items but with different labels and custom data properties
    // Problem: combobox cannot determine correct selected item
    const { key, namespace } = extractNamespaceKey(uniqueValue);
    const option = getFromKeyStore(key, namespace);

    if (!option) {
      // plural keys are not in the store by default, just add while editing
      addKey(key, namespace);
      setSelectedOption(createUniqueOption(key, namespace));
    } else {
      setSelectedOption(option);
    }
  };

  const isSavingDisabled =
    !lockStatusAction?.data?.canWrite || (changes.length === 0 && !isLoading);

  const error =
    lockStatusAction?.error || saveAction.error || translateAction.error;

  const dialog = (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogTitle className={'eye:sr-only'}>hidden</DialogTitle>
      <DialogContent maxWidth={'xl'} className='eye:w-[1200px]'>
        {error && <Alert variant={'error'}>{error.message}</Alert>}
        {lockStatusAction.data?.isLocked && (
          <Alert variant={'warning'}>
            {lockStatusAction.data?.canWrite ? (
              <>
                You are working in a locked environment. Only you can save
                changes. Do not forget to unlock it when you are finished.
              </>
            ) : (
              <>
                Saving translations is currently locked by another user. A
                deployment might be going on right now.
              </>
            )}
          </Alert>
        )}

        <DialogDescription>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className='eye:flex eye:items-center eye:justify-between eye:gap-x-xs eye:mb-xs'>
                {!hasRestrictions && (
                  <Toggle
                    className='eye:mt-12'
                    checked={
                      availableLanguages.length === targetLanguages.length
                    }
                    title='activate / deactivate all languages'
                    onCheckedChange={(checked) => {
                      setTargetLanguages(checked ? availableLanguages : []);
                    }}
                  />
                )}
                <Combobox
                  placeholder='Choose a key'
                  placeholderFilter='Search a key'
                  className='eye:w-[640px]'
                  labelProps={{ float: 'top', label: 'Key (Namespace)' }}
                  onSelect={onSelect}
                  value={selectedOption?.uniqueValue}
                  items={translationKeys}
                />
                <Combobox
                  shouldFilter={false}
                  labelProps={{ float: 'top', label: 'Engine' }}
                  onSelect={(value) => setEngine(value)}
                  value={engine}
                  items={translatorEngines()}
                />
                <Button
                  disabled={isSavingDisabled}
                  className='eye:mt-12'
                  size='sm'
                  onClick={() => void saveTranslations()}
                >
                  Save
                </Button>
                <span className='eye:mt-12'>
                  {changes.length} of {availableLanguages.length} changes
                </span>
              </div>

              {selectedOption?.key && (
                <div className='eye:max-h-[55vh] eye:overflow-y-scroll eye:flex eye:flex-col eye:gap-4xs'>
                  {baseKeyRef.current && (
                    <a
                      className={'eye:underline'}
                      href={'#'}
                      onClick={() =>
                        onSelect(
                          createUniqueValue({
                            key: baseKeyRef.current,
                            namespace: selectedOption.namespace,
                          }),
                        )
                      }
                    >
                      back to base key: {baseKeyRef.current}
                    </a>
                  )}
                  {selectedOption.hasPluralization && (
                    <>
                      <PluralizationOverview
                        pluralKeys={pluralKeysAllRef.current}
                        selectedOption={selectedOption}
                        onSelect={onSelect}
                      />
                      Fallback values are used if none of the plural forms
                      match:
                    </>
                  )}

                  {availableLanguages.map((language) => {
                    const isActive = targetLanguages.includes(language);
                    const canEdit = canEditLanguage(language);
                    const canTranslate =
                      user?.permissions?.inlineTranslations?.canTranslate ===
                        true && canEdit;

                    return (
                      <LanguageRow
                        key={language}
                        canEdit={canEdit}
                        isActive={isActive}
                        language={language}
                        translation={translations[language]}
                        translationOriginal={translationsOriginal[language]}
                        selectedOption={selectedOption}
                        onTextChange={onTextChange}
                        onRevertClick={onRevertClick}
                        onLanguageToggle={onLanguageToggle}
                        translate={translate}
                        canTranslate={canTranslate}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
  return createPortal(dialog, document.body);
}
